<script setup>
import { ref, watch } from 'vue';

const props = defineProps({
    modelValue: {
        type: String,
        default: ''
    },
    inputId: {
        type: String,
        default: ''
    },
    placeholder: {
        type: String,
        default: 'HH:mm'
    },
    label: {
        type: String,
        default: ''
    },
    required: {
        type: Boolean,
        default: false
    },
    disabled: {
        type: Boolean,
        default: false
    },
    marginTop: {
        type: String,
        default: '0'
    },
    marginBottom: {
        type: String,
        default: '0'
    },
    marginLeft: {
        type: String,
        default: '0'
    },
    marginRight: {
        type: String,
        default: '0'
    },
    labelRightmargin: {
        type: String,
        default: '2rem'
    },
    visible: {
        type: Boolean,
        default: true
    }
});

const emit = defineEmits(['update:modelValue', 'input', 'change']);

const timeInput = ref(props.modelValue);
const showTimePicker = ref(false);

const formatTimeInput = (value) => {
    const cleanedValue = value.replace(/\D/g, '').slice(0, 4); // 숫자만 허용하고 최대 길이 4
    if (cleanedValue.length >= 3) {
        let hours = parseInt(cleanedValue.slice(0, 2), 10);
        let minutes = cleanedValue.slice(2);

        if (hours >= 24) {
            hours = 0;
        }

        if (parseInt(minutes, 10) >= 60) {
            minutes = '00';
        }

        return `${hours.toString().padStart(2, '0')}:${minutes}`;
    } else if (cleanedValue.length >= 1) {
        return cleanedValue;
    }
    return '';
};

// HH:mm 형식 및 범위 확인 함수
const isValidTime = (time) => {
    const [hours, minutes] = time.split(':').map(Number);
    return hours >= 0 && hours < 24 && minutes >= 0 && minutes < 60;
};

const handleInput = (event) => {
    let formattedValue = formatTimeInput(event.target.value);

    // HH:mm 형식일 때만 처리
    if (formattedValue.length === 5) {
        if (isValidTime(formattedValue)) {
            // 유효한 시간일 경우만 emit
            timeInput.value = formattedValue;
            emit('update:modelValue', formattedValue);
            emit('input', formattedValue);
            emit('change', formattedValue);
        } else {
            // 잘못된 시간일 경우 초기화
            timeInput.value = '';
        }
    } else {
        timeInput.value = formattedValue; // 중간 입력 처리
    }
};

const handleTimeSelect = (time) => {
    timeInput.value = time;
    emit('update:modelValue', time);
    emit('input', time);
    emit('change', time);
    showTimePicker.value = false;
};

// 모델 값이 변경될 때 업데이트
watch(
    () => props.modelValue,
    (newValue) => {
        timeInput.value = newValue;
    }
);
</script>

<template>
    <div
        v-if="visible"
        class="flex items-center relative"
        :style="{
            marginTop: marginTop,
            marginBottom: marginBottom,
            marginLeft: marginLeft,
            marginRight: marginRight
        }"
    >
        <label v-if="label" :class="['text-base font-bold', required ? 'label-required' : 'text-gray-700 dark:text-gray-300']" :style="{ marginRight: labelRightmargin, flex: 'none' }">
            {{ label }}
        </label>
        <div class="relative flex items-center w-full">
            <input
                :id="inputId"
                :value="timeInput"
                @input="handleInput"
                maxlength="5"
                :disabled="disabled"
                :placeholder="placeholder"
                class="bg-gray-50 border border-gray-300 text-gray-900 text-base rounded-lg block w-24 text-center p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                required
            />
            <div class="absolute inset-y-0 end-0 top-0 flex items-center pe-3.5 pointer-events-none">
                <svg class="w-4 h-4 text-gray-400 dark:text-gray-400" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 24 24">
                    <path fill-rule="evenodd" d="M2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10S2 17.523 2 12Zm11-4a1 1 0 1 0-2 0v4a1 1 0 0 0 .293.707l3 3a1 1 0 0 0 1.414-1.414L13 11.586V8Z" clip-rule="evenodd" />
                </svg>
            </div>
        </div>
    </div>
</template>

<style scoped>
.label-required {
    color: #f87171; /* red-500 */
}

.label-required::after {
    content: '*';
    color: #f87171; /* red-500 */
}

/* 스타일 일관성을 위해 input 필드와 맞추기 */
input {
    width: 7rem; /* 고정된 너비 */
    text-align: center; /* 중앙 정렬 */
    padding: 0.75rem 1.5rem 0.75rem 0.75rem;
    font-size: 0.875rem;
    line-height: 1.25rem;
    border: 1px solid #d1d5db;
    border-radius: 0.5rem;
    background-color: white;
    color: #111827;
}

input:focus {
    border-color: #0038ea; /* 포커스 시 테두리 색상 */
    outline: none;
}

input::placeholder {
    color: #9ca3af; /* placeholder 색상 */
}
</style>
